import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Repeat } from 'lucide-react';

const DOW = [
  { iso: 1, short: 'Mon' },
  { iso: 2, short: 'Tue' },
  { iso: 3, short: 'Wed' },
  { iso: 4, short: 'Thu' },
  { iso: 5, short: 'Fri' },
  { iso: 6, short: 'Sat' },
  { iso: 7, short: 'Sun' },
];

export default function RecurrenceBuilder({
  pattern, setPattern,
  daysOfWeek, toggleDay,
  endMode, setEndMode,
  endDate, setEndDate,
  endAfterCount, setEndAfterCount,
  skipHolidays, setSkipHolidays,
  skipFridayPrayer, setSkipFridayPrayer,
  startDate, // start_date is the booking_date in the parent state
}) {
  return (
    <Card className="p-4 space-y-4 border-emerald-200 bg-emerald-50/40" data-testid="recurrence-builder">
      <div className="flex items-center gap-2">
        <Repeat className="w-4 h-4 text-emerald-700" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-900">Recurrence</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Pattern</Label>
          <Select value={pattern} onValueChange={setPattern}>
            <SelectTrigger data-testid="recurrence-pattern"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="biweekly">Every 2 weeks (biweekly)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Series Starts</Label>
          <Input value={startDate || ''} disabled className="bg-white" data-testid="series-start-display" />
          <p className="text-[11px] text-muted-foreground mt-1">First occurrence uses the date above.</p>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Repeat on</Label>
        <div className="flex flex-wrap gap-2">
          {DOW.map((d) => {
            const active = daysOfWeek.includes(d.iso);
            return (
              <button
                key={d.iso}
                type="button"
                onClick={() => toggleDay(d.iso)}
                data-testid={`dow-${d.iso}`}
                className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
              >
                {d.short}
              </button>
            );
          })}
        </div>
        {daysOfWeek.length === 0 && (
          <p className="text-[11px] text-amber-700 mt-1">Pick at least one day.</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">End Series</Label>
          <Select value={endMode} onValueChange={setEndMode}>
            <SelectTrigger data-testid="end-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="end_date">On date</SelectItem>
              <SelectItem value="end_after_count">After N occurrences</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          {endMode === 'end_date' ? (
            <>
              <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                data-testid="series-end-date"
              />
            </>
          ) : (
            <>
              <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Number of occurrences</Label>
              <Input
                type="number"
                min={2}
                max={52}
                value={endAfterCount}
                onChange={(e) => setEndAfterCount(e.target.value)}
                placeholder="e.g. 8"
                data-testid="series-end-count"
              />
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 pt-2 border-t border-emerald-200">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={skipHolidays}
            onChange={(e) => setSkipHolidays(e.target.checked)}
            data-testid="skip-holidays"
          />
          Skip Brunei public holidays
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={skipFridayPrayer}
            onChange={(e) => setSkipFridayPrayer(e.target.checked)}
            data-testid="skip-friday-prayer"
          />
          Skip Friday prayer (12:00–14:00)
        </label>
      </div>

      <p className="text-[11px] text-muted-foreground italic">
        Series MVP: weekly/biweekly only · cash, complimentary, or pay later (hold) · max 183 days · max 52 occurrences ·
        conflicting dates skipped automatically.
      </p>
    </Card>
  );
}
